import React from 'react';
import InputBase from '@mui/material/InputBase';
import InputMask from 'react-input-mask';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import commons from '../../../assets/styles/commons';
import colors from '../../../assets/styles/colors';
import { Control, Controller, UseControllerProps } from 'react-hook-form';
import { FormHelperText, styled } from '@material-ui/core';
import InputAdornment from '@mui/material/InputAdornment';

type InputElementType = HTMLInputElement | HTMLTextAreaElement;

interface BasicInputProps<T extends InputElementType = HTMLInputElement> {
  label?: string;
  name?: string;
  id?: string;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  step?: string;
  onChange?: (event: React.ChangeEvent<T>) => void;
  value?: number | string;
  defaultValue?: number | string;
  placeholder?: string;
  mask?: string;
  errorMessage?: string;
  size?: 'small' | 'medium';
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  onBlur?: (event: React.FocusEvent<T>) => void;
  maxLength?: number;
  type?: string;
  labelColor?: string;
  className?: string;
  error?: boolean;
  helperText?: string;
}

interface HookFormSpecificProps<T extends InputElementType = HTMLInputElement>
  extends Omit<UseControllerProps, 'defaultValue' | 'onChange' | 'onBlur'> {
  control: Control<any>;
  name: string;
  onChange?: (event: React.ChangeEvent<T>) => void;
  onBlur?: (event: React.FocusEvent<T>) => void;
}

type FormInputProps<T extends InputElementType = HTMLInputElement> =
  BasicInputProps<T> & Partial<HookFormSpecificProps<T>>;

const CustomInput = styled(InputBase)(({ theme }) => ({
  'label + &': {
    marginTop: theme.spacing(3),
    color: colors.neutral.gray,
  },

  '&.MuiInputBase-root ': {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: colors.secondary.softBlue,
    border: '1px solid',
    borderColor: colors.primary.orange,
    '&.Mui-error': {
      borderColor: colors.error.intenseRed,
    },
    flex: 1,
    width: 'auto',
    height: 'auto',
    padding: '5px 12px',
    transition: theme.transitions.create([
      'border-color',
      'background-color',
      'box-shadow',
    ]),
    fontFamily: commons.fontFamily,
    fontVariant: 'lining-nums',
    '&:focus-within': {
      boxShadow: `rgba(255, 114, 6, 0.25) 0 0 0 0.2rem`,
      borderColor: colors.primary.orange,
    },
    '&.Mui-focused': {
      color: colors.primary.orange,
    },
  },

  '& .MuiTypography-root': {
    fontFamily: commons.fontFamily,
    color: colors.primary.orange,
    fontSize: 18,
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },

  '& .MuiInputAdornment-positionStart': {
    marginRight: 0,
  },
}));

const Input = <T extends InputElementType = HTMLInputElement>({
  label,
  id,
  style,
  placeholder,
  mask,
  size = 'small',
  name,
  control,
  startAdornment,
  endAdornment,
  value,
  defaultValue,
  onChange,
  maxLength,
  type,
  labelColor = 'orange',
  className,
  error: directError,
  helperText: directHelperText,
  onBlur: directOnBlur,
}: FormInputProps<T>) => {
  const getLabelColor = () => {
    if (labelColor === 'white') {
      return colors.base;
    }
    return colors.primary.orange;
  };

  if (control && name) {
    return (
      <Box component="div" sx={{ display: 'grid' }} className={className}>
        <Controller
          name={name}
          control={control}
          render={({
            field: {
              ref,
              onBlur: rhfOnBlur,
              onChange: rhfOnChange,
              value: rhfValue,
            },
            fieldState: { error: fieldError },
          }) => (
            <FormControl size={size} error={!!fieldError}>
              <InputLabel
                variant="standard"
                shrink
                htmlFor={id}
                sx={{
                  fontFamily: commons.fontFamily,
                  color: getLabelColor(),
                  fontSize: 18,
                  '&.Mui-focused': {
                    color: getLabelColor(),
                  },
                  ...style,
                }}
              >
                {label}
              </InputLabel>
              {mask ? (
                <InputMask
                  mask={mask}
                  value={value || rhfValue}
                  onChange={onChange || (rhfOnChange as any)}
                  onBlur={directOnBlur || (rhfOnBlur as any)}
                >
                  {(inputProps: any) => (
                    <CustomInput
                      {...inputProps}
                      id={id}
                      placeholder={placeholder}
                      type={type}
                      error={!!fieldError}
                      inputProps={{ maxLength, ref }}
                      endAdornment={
                        <InputAdornment position="end">
                          {endAdornment}
                        </InputAdornment>
                      }
                      startAdornment={
                        <InputAdornment position="start">
                          {startAdornment}
                        </InputAdornment>
                      }
                    />
                  )}
                </InputMask>
              ) : (
                <>
                  <CustomInput
                    value={value || rhfValue}
                    onChange={onChange || (rhfOnChange as any)}
                    id={id}
                    placeholder={placeholder}
                    ref={ref}
                    onBlur={directOnBlur || (rhfOnBlur as any)}
                    type={type}
                    inputProps={{ maxLength }}
                    error={!!fieldError}
                    endAdornment={
                      <InputAdornment position="end">
                        {endAdornment}
                      </InputAdornment>
                    }
                    startAdornment={
                      <InputAdornment position="start">
                        {startAdornment}
                      </InputAdornment>
                    }
                  />
                </>
              )}
              {!!fieldError && (
                <FormHelperText style={{ color: colors.error.intenseRed }}>
                  {fieldError?.message}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      </Box>
    );
  }

  return (
    <Box component="div" sx={{ display: 'grid' }} className={className}>
      <FormControl size={size} error={directError}>
        <InputLabel
          variant="standard"
          shrink
          htmlFor={id}
          sx={{
            fontFamily: commons.fontFamily,
            color: getLabelColor(),
            fontSize: 18,
            '&.Mui-focused': {
              color: getLabelColor(),
            },
            ...style,
          }}
        >
          {label}
        </InputLabel>
        {mask ? (
          <InputMask
            mask={mask}
            value={value}
            onChange={onChange as any}
            onBlur={directOnBlur as any}
          >
            {(inputProps: any) => (
              <CustomInput
                {...inputProps}
                id={id}
                placeholder={placeholder}
                type={type}
                error={directError}
                inputProps={{ maxLength }}
                endAdornment={
                  <InputAdornment position="end">{endAdornment}</InputAdornment>
                }
                startAdornment={
                  <InputAdornment position="start">
                    {startAdornment}
                  </InputAdornment>
                }
              />
            )}
          </InputMask>
        ) : (
          <>
            <CustomInput
              value={value}
              defaultValue={defaultValue}
              onChange={onChange as any}
              id={id}
              placeholder={placeholder}
              onBlur={directOnBlur as any}
              type={type}
              inputProps={{ maxLength }}
              error={directError}
              endAdornment={
                <InputAdornment position="end">{endAdornment}</InputAdornment>
              }
              startAdornment={
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              }
            />
          </>
        )}
        {directHelperText && (
          <FormHelperText style={{ color: colors.error.intenseRed }}>
            {directHelperText}
          </FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};

export default Input;
